// src/pages/AdminForm.js
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createAdmin, updateAdmin } from "../../redux/slices/adminSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { showNotification } from "../../redux/slices/notificationSlice";
import { useTranslation } from "../../contexts/LanguageContext";

function AdminForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { admin } = useSelector((state) => state.auth);

  // Always call hooks at the top
  const editAdmin = location.state?.admin || null;

  // Initialize form data. If in edit mode, prefill with admin details.
  const [formData, setFormData] = useState({
    name: editAdmin?.name || "",
    email: editAdmin?.email || "",
    phone: editAdmin?.phone || "",
    password: "",
    // Store the base64 string for profile picture
    profilePicture: editAdmin?.profilePicture || "",
    permissions: editAdmin?.permissions || {
      can_manage_admins: false,
      can_manage_analytics: false,
      can_manage_modules: false,
      can_manage_reports: false,
      can_manage_categories: false,
      can_manage_banners: false,
      can_manage_marketing: false,
      can_manage_payments: false,
      can_manage_tickets: false,
      can_manage_settings: false,
      can_manage_delivery: false,
      can_manage_users: false,
      can_manage_subscriptions: false,
      can_manage_orders: false,
      can_manage_countries: false,
      can_manage_packages: false,
      can_manage_shippers: false,
    },
  });

  // Local state for showing an image preview
  const [imagePreview, setImagePreview] = useState(editAdmin?.profilePicture || "");

  // Helper function: convert file to Base64 string
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  useEffect(() => {
    if (editAdmin) {
      setFormData({
        name: editAdmin.name || "",
        email: editAdmin.email || "",
        phone: editAdmin.phone || "",
        password: "",
        profilePicture: editAdmin.profilePicture || "",
        permissions: editAdmin.permissions || {
          can_manage_admins: false,
          can_manage_analytics: false,
          can_manage_modules: false,
          can_manage_reports: false,
          can_manage_categories: false,
          can_manage_banners: false,
          can_manage_marketing: false,
          can_manage_payments: false,
          can_manage_tickets: false,
          can_manage_settings: false,
          can_manage_delivery: false,
          can_manage_users: false,
          can_manage_subscriptions: false,
          can_manage_orders: false,
          can_manage_countries: false,
          can_manage_packages: false,
          can_manage_shippers: false,
        },
      });
      setImagePreview(editAdmin.profilePicture || "");
    }
  }, [editAdmin]);

  // Permission check (render after hooks have run)
  if (!admin?.permissions?.can_manage_admins) {
    return <div>{t("adminForm.unauthorized")}</div>;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePermissionChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      permissions: { ...prev.permissions, [name]: checked },
    }));
  };

  // Handle file input change: convert file to Base64 and set preview
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const base64 = await convertFileToBase64(file);
        setFormData((prev) => ({ ...prev, profilePicture: base64 }));
        setImagePreview(base64);
      } catch (error) {
        dispatch(showNotification({ message: t("adminForm.imageError"), type: "error" }));
      }
    }
  };
 
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Send as JSON with base64 image (backend expects this format)
    const dataToSend = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      permissions: formData.permissions
    };
    
    if (formData.password) {
      dataToSend.password = formData.password;
    }
    
    if (formData.profilePicture) {
      dataToSend.profilePicture = formData.profilePicture;
    }
    
    if (editAdmin) {
      dispatch(updateAdmin({ id: editAdmin._id, updatedData: dataToSend }))
        .unwrap()
        .then(() => {
          dispatch(showNotification({ message: t("adminForm.updatedSuccess"), type: "success" }));
          navigate("/admins-rights");
        })
        .catch((err) => {
          console.error("Failed to update admin:", err);
        });
    } else {
      dispatch(createAdmin(dataToSend))
        .unwrap()
        .then(() => {
          dispatch(showNotification({ message: t("adminForm.createdSuccess"), type: "success" }));
          navigate("/admins-rights");
        })
        .catch((err) => {
          console.error("Failed to create admin:", err);
        });
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 bg-white shadow-md rounded-md">
      <h1 className="font-semibold text-2xl text-themeDark mb-4">{editAdmin ? t("adminForm.updateTitle") : t("adminForm.createTitle")}</h1>
      <form onSubmit={handleSubmit}>
        {/* Basic Info */}
        <div className="mb-4">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            {t("adminForm.name")}
          </label>
          <input
            className="mt-1 block w-full border border-gray-300 rounded-md p-2"
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
          />
        </div>
        
        <div className="mb-4">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            {t("adminForm.email")}
          </label>
          <input
            className="mt-1 block w-full border border-gray-300 rounded-md p-2"
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="mb-4">
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
            {t("adminForm.phone")}
          </label>
          <input
            className="mt-1 block w-full border border-gray-300 rounded-md p-2"
            type="text"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="mb-4">
          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
            {editAdmin ? t("adminForm.passwordEdit") : t("adminForm.password")}
          </label>
          <input
            className="mt-1 block w-full border border-gray-300 rounded-md p-2"
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            {...(editAdmin ? {} : { required: true })}
          />
        </div>

        {/* Profile Picture Upload */}
        <div className="mb-4">
          <label htmlFor="profilePicture" className="block text-sm font-medium text-gray-700">
            {t("adminForm.profilePicture")}
          </label>
          <input
            className="mt-1 block w-full"
            type="file"
            id="profilePicture"
            accept="image/*"
            onChange={handleFileChange}
          />
          {imagePreview && (
            <img
              src={imagePreview}
              alt="Profile Preview"
              className="mt-2 h-16 w-16 rounded-full object-cover"
            />
          )}
        </div>

        {/* Permissions */}
        <div className="mb-4">
          <h2 className="text-xl font-semibold mb-2">{t("adminForm.permissions")}</h2>
          <div className="grid grid-cols-2 gap-2">
            {Object.keys(formData.permissions).map((permKey) => {
              // Try the translated label; fall back to the raw key if none is defined.
              const labelKey = `adminForm.perm.${permKey}`;
              const translated = t(labelKey);
              const label = translated === labelKey ? permKey : translated;
              return (
                <label key={permKey} className="flex items-center space-x-2 text-sm">
                  <input
                    type="checkbox"
                    name={permKey}
                    checked={formData.permissions[permKey]}
                    onChange={handlePermissionChange}
                  />
                  <span>{label}</span>
                </label>
              );
            })}
          </div>
        </div>

        <button
          type="submit"
          className="bg-themeOrange hover:bg-themeOrange-dark text-white px-4 py-2 rounded-md text-sm font-medium"
        >
          {editAdmin ? t("adminForm.updateButton") : t("adminForm.createButton")}
        </button>
      </form>
    </div>
  );
}

export default AdminForm;
